/**
 * @module AdapterDomainIcon
 * @package @geenius/adapters/react
 * @description Maps shared adapter domain icon identifiers to React SVG slots.
 */

import type { AdapterDomain } from "@geenius/adapters";
import { DOMAIN_ICONS } from "@geenius/adapters";
import type { JSX } from "react";
import { cn } from "../lib/utils";

const ICON_PATHS: Record<string, JSX.Element> = {
  "credit-card": (
    <>
      <rect height="14" rx="2" width="18" x="3" y="5" />
      <path d="M3 10h18" />
      <path d="M7 15h3" />
    </>
  ),
  database: (
    <>
      <ellipse cx="12" cy="5" rx="7" ry="3" />
      <path d="M5 5v6c0 1.66 3.13 3 7 3s7-1.34 7-3V5" />
      <path d="M5 11v6c0 1.66 3.13 3 7 3s7-1.34 7-3v-6" />
    </>
  ),
  "file-text": (
    <>
      <path d="M14 3v5h5" />
      <path d="M6 3h8l5 5v13H6z" />
      <path d="M9 13h6" />
      <path d="M9 17h6" />
    </>
  ),
  folder: (
    <>
      <path d="M3 7h7l2 2h9v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <path d="M3 7V5a2 2 0 0 1 2-2h4l2 2h5" />
    </>
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2v3" />
      <path d="M12 19v3" />
      <path d="m4.93 4.93 2.12 2.12" />
      <path d="m16.95 16.95 2.12 2.12" />
      <path d="M2 12h3" />
      <path d="M19 12h3" />
      <path d="m4.93 19.07 2.12-2.12" />
      <path d="m16.95 7.05 2.12-2.12" />
    </>
  ),
  "shield-check": (
    <>
      <path d="M12 3 5 6v5c0 4.2 2.75 7.7 7 10 4.25-2.3 7-5.8 7-10V6z" />
      <path d="m9 12 2 2 4-4" />
    </>
  ),
  sparkles: (
    <>
      <path d="m12 3 1.7 4.3L18 9l-4.3 1.7L12 15l-1.7-4.3L6 9l4.3-1.7z" />
      <path d="m6 15 .8 2.2L9 18l-2.2.8L6 21l-.8-2.2L3 18l2.2-.8z" />
      <path d="m18 14 .6 1.4L20 16l-1.4.6L18 18l-.6-1.4L16 16l1.4-.6z" />
    </>
  ),
};

const FALLBACK_ICON = (
  <>
    <circle cx="12" cy="12" r="8" />
    <path d="M12 8v4" />
    <path d="M12 16h.01" />
  </>
);

export function AdapterDomainIcon({
  className,
  domain,
}: {
  className?: string;
  domain: AdapterDomain;
}): JSX.Element {
  const iconId = DOMAIN_ICONS[domain] ?? "";

  return (
    <svg
      aria-hidden="true"
      className={cn("h-6 w-6", className)}
      fill="none"
      focusable="false"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.8}
      viewBox="0 0 24 24"
    >
      {ICON_PATHS[iconId] ?? FALLBACK_ICON}
    </svg>
  );
}
